import React, { useEffect, useMemo, useState } from 'react'
import {
  Tabs,
  TabsBody,
  TabPanel,
} from '@material-tailwind/react/components/Tabs'
import { PageProps } from 'gatsby'
import PremiumTemplate from '../premiumTemplate'
import FirmOfferTemplate from './firmOfferTemplate'
import Teams from '../teams'
import TeamsColor from '../teamsColor'
import { GroupWithLogo } from '../../../types/Groupement'
import { fetchGroupByFirmId } from '../../groups/requests'
import { getDefaultActiveTab } from '../../../Utils/functionnals'
import { entriesProps } from '../firmTemplate'
import { TabsHeaderCommon } from '../../common/tabs/TabsHeaderCommon'
import { isAttractivitySubscription } from '../../../Utils/firm/subscription'
import { getIntershipAndAlternanceOffers, getAllOffersExceptedIntershipAndAlternance } from '../../../Utils/offers/contractTypes'

type TabProps = {
  entry: entriesProps
  color: string | null
  constants: {
    siteStrapiUrl: string | null
    frontStrapiToken: string | null
  }
  location: PageProps['location']
}

const FirmTab: React.FC<TabProps> = ({ entry, color, constants, location }) => {
  const [activeTab, setActiveTab] = useState<string>(
    getDefaultActiveTab(location) || 'profil',
  )

  const isAttractivity = useMemo(() => isAttractivitySubscription({
    subscription: entry.content.subscription,
    isPremium: entry.content.premium || false,
  }), [entry.content.subscription, entry.content.premium])

  const tabsTitle: {
    label: string
    key: string
    pill?: number
  }[] = [
      {
        label: 'Profil',
        key: 'profil',
      },
    ]

  const withTeam = entry.content.teams && entry.content.teams.length > 0

  /**
   * If the firm is attractivity, we display the first offer and the intership and alternance offers
   * If the firm is not attractivity, we display all offers
   */
  const firmOffers = useMemo(() => {
    const intershipAlternanceOffers = getIntershipAndAlternanceOffers(entry.content.offers || [])
    const allOffersExceptedIntershipAndAlternance = getAllOffersExceptedIntershipAndAlternance(entry.content.offers || [])
      .sort((a, b) => new Date(b.publishedAt as string).getTime() - new Date(a.publishedAt as string).getTime())

    if (isAttractivity) {
      const firstOffer = allOffersExceptedIntershipAndAlternance?.[0]
      if (firstOffer) {
        return [
          firstOffer,
          ...intershipAlternanceOffers,
        ]
      }
      return intershipAlternanceOffers
    }
    return entry.content.offers
  }, [entry.content.offers, isAttractivity])

  const withOffer = firmOffers && firmOffers.length > 0

  if (withTeam) {
    tabsTitle.push({
      label: 'Equipes',
      key: 'teams',
    })
  }

  if (withOffer) {
    tabsTitle.push({
      label: 'Offres',
      key: 'offers',
      pill: firmOffers?.length || undefined,
    })
  }
  const [groups, setGroups] = useState<GroupWithLogo[]>([])

  useEffect(() => {
    const fetchGroups = async () => {
      if (
        constants.siteStrapiUrl &&
        constants.frontStrapiToken &&
        !groups?.length &&
        entry.content.id
      ) {
        const result = await fetchGroupByFirmId({
          strapiUrl: constants.siteStrapiUrl,
          token: constants.frontStrapiToken,
          firmId: entry.content.id,
        })
        setGroups(result.list || [])
      }
    }

    fetchGroups()
  }, [constants.siteStrapiUrl, constants.frontStrapiToken])

  return (
    <Tabs
      className="xl:w-[1115px] lg:w-[950px] 2xl:w-[1320px] mx-auto mb-14 lg:mb-20"
      value={activeTab}
    >
      <TabsHeaderCommon
        tabsTitle={tabsTitle}
        setActiveTab={setActiveTab}
        color={color || undefined}
      />
      <TabsBody>
        <TabPanel value="profil" className="p-0 mt-2">
          <PremiumTemplate
            firm={entry.content}
            googleMapsApiKey={entry.googleMapsApiKey || ''}
            youTubeApiKey={entry.youTubeApiKey}
            color={color || null}
            groups={groups}
          />
        </TabPanel>
        {withTeam && (
          <TabPanel value="teams">
            {color ? (
              <TeamsColor
                teams={entry.content.teams}
                youTubeApiKey={entry.youTubeApiKey}
                color={color || null}
              />
            ) : (
              <Teams
                teams={entry.content.teams}
                youTubeApiKey={entry.youTubeApiKey}
              />
            )}
          </TabPanel>
        )}
        {withOffer && (
          <TabPanel value="offers">
            <FirmOfferTemplate
              offers={
                firmOffers?.map((offer) => ({
                  ...offer,
                  jobType: offer.jobType?.value,
                  companyName: entry.content.name,
                  url: color
                    ? `/${entry.content.slug}/offres/${offer.slug}`
                    : `/cabinets/${entry.content.slug}/offres/${offer.slug}`,
                  city: offer.office.city || undefined,
                  salary: offer.payRange || undefined,
                  startDate: offer.startDate || undefined,
                  headerImage:
                    entry.content.headerUrl ||
                    entry.content.header ||
                    undefined,
                  logo:
                    entry.content?.logoUrl || entry.content?.logo || undefined,
                  title: offer.title,
                  color: color || undefined,
                  contractType: offer.contractType || undefined,
                })) || []
              }
              color={color || undefined}
            />
          </TabPanel>
        )}
      </TabsBody>
    </Tabs>
  )
}

export default FirmTab
